import * as React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "react-toastify";
import TopBarProgress from "react-topbar-progress-indicator";
import { BusinessReportPrompt, BusinessReportType } from "@/modules/snapshots/businessReports.types";
import { BusinessReportPromptDisplay } from "@/modules/snapshots/components/BusinessReportPromptForm";
import { BusinessReportPromptEditModal } from "@/modules/snapshots/components/BusinessReportPromptEditModal";
import type { PromptEditFormInputs } from "@/modules/snapshots/components/BusinessReportPromptEditModal.utils";
import { useUpdateBusinessReportPromptMutation } from "@/modules/snapshots/businessReports.api";

interface Props {
  prompt: BusinessReportPrompt;
  isExpanded: boolean;
  onToggle: () => void;
  setPrompts: React.Dispatch<React.SetStateAction<BusinessReportPrompt[]>>;
  templateType?: BusinessReportType;
}

export const BusinessReportDetailsRow: React.FC<Props> = ({
  setPrompts,
  prompt,
  isExpanded,
  onToggle,
  templateType,
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [handleUpdate, { isLoading }] = useUpdateBusinessReportPromptMutation();

  const onSave = async (payload: PromptEditFormInputs) => {
    await handleUpdate({ id: prompt.id, data: payload }).unwrap();
    setPrompts(prevPrompts => prevPrompts.map(p => (p.id === prompt.id ? { ...p, ...payload } : p)));
    toast.success("Change saved successfully!");
  };

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  return (
    <>
      <div className="overflow-hidden rounded-lg border border-[#ddd5cb] bg-white transition-colors hover:border-[#c9b39c]">
        {isLoading && <TopBarProgress />}
        <button
          type="button"
          aria-expanded={isExpanded}
          className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left transition-colors hover:bg-[#f7f2ec] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-desert-sand-300"
          onClick={onToggle}
        >
          <span className="flex min-w-0 items-center gap-3">
            <span className="flex size-8 flex-shrink-0 items-center justify-center rounded-md bg-[#f1e2ce] text-sm font-semibold text-desert-sand-700">
              {prompt.order + 1}
            </span>
            <span className="truncate text-sm font-semibold text-[#111b35]">{prompt.name}</span>
          </span>
          {isExpanded ? (
            <ChevronUp className="size-4 flex-shrink-0 text-gray-500" aria-hidden="true" />
          ) : (
            <ChevronDown className="size-4 flex-shrink-0 text-gray-500" aria-hidden="true" />
          )}
        </button>
        {isExpanded && (
          <div className="border-t border-[#ebe5de]">
            <BusinessReportPromptDisplay prompt={prompt} onEdit={handleEdit} templateType={templateType} />
          </div>
        )}
      </div>

      <BusinessReportPromptEditModal
        prompt={prompt}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={onSave}
        isLoading={isLoading}
        templateType={templateType}
      />
    </>
  );
};
